/**
 * "Then by" (KPR-1636, KPE-051 ruling i05): a list sorts by up to three
 * levels. Level 1 is the list's `sortField` / `sortDirection` and stays
 * exactly what it was; levels 2 and 3 are `sortThen`, a list of
 * `{ field, direction }` in order. Each level breaks the ties the levels
 * before it leave. This is the one place the levels change shape between the
 * three places they live:
 *
 * - the ADDRESS (utils/itemsView `ITEMS_VIEW_PARAMS`): `?sort2=&dir2=&sort3=&dir3=`;
 * - the LIST REQUEST (keepr-api docs/SCHEMA.md "Then by"): `sort_field_2`,
 *   `sort_direction_2`, `sort_field_3`, `sort_direction_3`;
 * - a SAVED FILTER (keepr-api § Saved filters): `sort_then: [{ field, direction }]`,
 *   `null` when it has none (every filter saved before the levels existed).
 *
 * The API refuses a fourth level, a level 3 without a level 2 and a direction
 * other than asc / desc, so everything here is normalised before it is sent:
 * at most two further levels, compacted, each direction 'asc' or 'desc'.
 */

/** Level 1 plus at most two more. */
export const MAX_SORT_LEVELS = 3;
const MAX_THEN = MAX_SORT_LEVELS - 1;

const directionOf = raw => (`${raw ?? ''}`.trim().toLowerCase() === 'asc' ? 'asc' : 'desc');

/**
 * Any list of levels as `[{ field, direction }]`: blanks dropped, the rest
 * compacted in order (a level 3 with no level 2 becomes level 2), at most two,
 * and anything but 'asc' read as 'desc' (the API's default, as at level 1).
 * Never null; a value that is not a list is no levels.
 */
export function sortThenOf(levels) {
	if (!Array.isArray(levels)) return [];
	return levels
		.map(level => ({ field: typeof level?.field === 'string' ? level.field.trim() : '', direction: directionOf(level?.direction) }))
		.filter(level => level.field)
		.slice(0, MAX_THEN);
}

/** Two lists of levels say the same order. */
export const sameSortThen = (a, b) => {
	const x = sortThenOf(a);
	const y = sortThenOf(b);
	return x.length === y.length && x.every((level, i) => level.field === y[i].field && level.direction === y[i].direction);
};

/**
 * The address patch that writes `levels` (hooks/useUrlParams `update`):
 * every one of the four params is named, so a level that goes away is
 * deleted with the rest in ONE write (react-router 6 hands a second update in
 * the same event the last render's address). `desc` is the params' default
 * and so is deleted rather than written unless the caller keeps defaults.
 */
export function sortThenPatch(levels) {
	const [two, three] = sortThenOf(levels);
	return {
		sort2: two?.field,
		dir2: two ? two.direction : undefined,
		sort3: three?.field,
		dir3: three ? three.direction : undefined,
	};
}

/** The list request's params for `levels`: `{}` for none, so a one-level request is byte-identical to before. */
export function sortThenParams(levels) {
	const params = {};
	sortThenOf(levels).forEach((level, i) => {
		params[`sort_field_${i + 2}`] = level.field;
		params[`sort_direction_${i + 2}`] = level.direction;
	});
	return params;
}

/** A saved filter's `sort_then` for `levels`: null for none (the API stores nothing). */
export function sortThenPayload(levels) {
	const out = sortThenOf(levels);
	return out.length ? out : null;
}
